import { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n';
import TranslatedText from './TranslatedText';
import axios from 'axios';
import { Leaf, Map, MapPin, Home, History, Siren, User, Info, TrendingDown, Calendar, Bell, Shield, Ship, Menu, X, AlertTriangle, Waves, BookOpen, CloudSun, Compass, Sprout, GraduationCap, Users, Radar, Megaphone, Moon, Target } from 'lucide-react';
import VolunteerNameModal from './VolunteerNameModal';
import FlexibleInputForm from './FlexibleInputForm';
import SOSSection from './SOSSection';
import StoricoSection from './StoricoSection';
import MapSection from './MapSection';
import ProfiloSection from './ProfiloSection';
import InfoSection from './InfoSection';
import AttivitaSection from './AttivitaSection';
import AdminSection from './AdminSection';
import FishingForLitterSection from './FishingForLitterSection';
import SegnalazioniSection from './SegnalazioniSection';
import SubacqueiSection from './SubacqueiSection';
import CalendarSection from './CalendarSection';
import DonationPopup from './DonationPopup';
import MagazineSection from './MagazineSection';
import MeteoMareSection from './MeteoMareSection';
import PlacesSection from './PlacesSection';
import FloraFaunaSection from './FloraFaunaSection';
import LanguageSelector from './LanguageSelector';
import MareDuSection from './MareDuSection';
import CommunitySection from './CommunitySection';
import ROVSection from './ROVSection';
import AnnouncementSection from './AnnouncementSection';
import MareLunaSection from './MareLunaSection';
import GamificationSection from './GamificationSection';
import EmmeAssistant, { EmmeButton, EmmeWelcomePopup } from './EmmeAssistant';
import EmmeGuide, { useEmmeGuide, EmmeHelpButton } from './EmmeGuide';
import UnreadAnnouncementPopup from './UnreadAnnouncementPopup';
import QuestionnaireForm from './QuestionnaireForm';
import MyOrganizationArea from './MyOrganizationArea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Verifica se le notifiche sono supportate
const isNotificationSupported = () => {
  return typeof window !== 'undefined' && 'Notification' in window;
};

// Richiedi permesso notifiche
const requestNotificationPermission = async () => {
  if (!isNotificationSupported()) {
    return false;
  }
  if (window.Notification.permission === 'default') {
    const permission = await window.Notification.requestPermission();
    return permission === 'granted';
  }
  return window.Notification.permission === 'granted';
};

// Mostra notifica nativa con vibrazione (simula onde)
const showNativeNotification = async (title, body, count) => {
  // Prova con Service Worker per notifiche persistenti
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'SHOW_NOTIFICATION',
      title: title,
      body: body,
      badge: count
    });
  } else if (isNotificationSupported() && window.Notification.permission === 'granted') {
    // Fallback: notifica normale
    new window.Notification(title, {
      body: body,
      icon: 'https://customer-assets.emergentagent.com/job_respiro-dashboard/artifacts/likcbe5b_IMG_6596.jpeg',
      tag: 'respiro-blu'
    });
  }
};

// Funzione per aggiornare badge PWA
const updateAppBadge = async (count) => {
  try {
    if ('setAppBadge' in navigator) {
      if (count > 0) {
        await navigator.setAppBadge(count);
      } else {
        await navigator.clearAppBadge();
      }
    }
  } catch (e) {
    console.log('Badge API non supportata');
  }
};

// Componente GPS Badge isolato - si aggiorna indipendentemente
const GPSBadge = memo(({ gpsRef }) => {
  const [display, setDisplay] = useState({ active: false, accuracy: 0 });

  useEffect(() => {
    const interval = setInterval(() => {
      if (gpsRef.current) {
        setDisplay({
          active: true,
          accuracy: gpsRef.current.accuracy || 0
        });
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [gpsRef]);

  if (!display.active) return null;

  return (
    <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-emerald-900/30 border border-emerald-500/30 rounded-full">
      <MapPin className="w-4 h-4 text-emerald-400 animate-pulse" />
      <span className="text-emerald-400 text-sm font-medium">
        GPS Attivo {display.accuracy > 0 && `(±${Math.round(display.accuracy)}m)`}
      </span>
    </div>
  );
});

GPSBadge.displayName = 'GPSBadge';

// Stats Cards isolate - ESG invece di Blue Credits
const StatsCards = memo(({ stats, t }) => (
  <div className="grid grid-cols-2 gap-4 mb-8">
    <div className="bg-slate-900/60 backdrop-blur-xl border border-emerald-500 rounded-2xl p-6">
      <div className="flex items-center gap-3 mb-3">
        <Leaf className="w-6 h-6 text-emerald-400" />
        <h3 className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">{t('home.totalKg')}</h3>
      </div>
      <div className="text-3xl font-bold text-white">{stats.total_kg.toFixed(1).replace('.', ',')}</div>
    </div>
    <div className="bg-slate-900/60 backdrop-blur-xl border border-violet-500 rounded-2xl p-6">
      <div className="flex items-center gap-3 mb-3">
        <TrendingDown className="w-6 h-6 text-violet-400" />
        <h3 className="text-xs font-semibold text-violet-400 uppercase tracking-wider">{t('home.co2Saved')}</h3>
      </div>
      <div className="text-3xl font-bold text-white">{(stats.total_kg * 0.15).toFixed(1).replace('.', ',')} <span className="text-lg">kg</span></div>
    </div>
  </div>
));

StatsCards.displayName = 'StatsCards';

const MainApp = () => {
  const { user, token } = useAuth();
  const { t, language } = useLanguage();
  const [currentView, setCurrentView] = useState('gamification'); // Nuova home = gamification
  const [stats, setStats] = useState({ total_kg: 0, total_credits: 0, total_collections: 0 });
  const [volunteerName, setVolunteerName] = useState(null);
  const [showNameModal, setShowNameModal] = useState(false);
  const [nameLoading, setNameLoading] = useState(true);
  const [notificationCount, setNotificationCount] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showNavMenu, setShowNavMenu] = useState(false);
  const [eventInvites, setEventInvites] = useState([]);
  const [showInvitesDropdown, setShowInvitesDropdown] = useState(false);
  const [showDonationPopup, setShowDonationPopup] = useState(false);
  const [myOrganization, setMyOrganization] = useState(null);  // Organizzazione utente
  const [analyticsStats, setAnalyticsStats] = useState(null);  // Statistiche admin
  const [showTurtle, setShowTurtle] = useState(false);  // Tartaruga AI
  const [showEmmeWelcome, setShowEmmeWelcome] = useState(false);  // Popup benvenuto Emme
  const prevNotificationCount = useRef(0);
  
  // Guida Emme per la Home (Raccolta)
  const { showGuide: showHomeGuide, closeGuide: closeHomeGuide, openGuide: openHomeGuide } = useEmmeGuide('raccolta');
  
  // GPS usa REF invece di STATE per non causare re-render
  const gpsRef = useRef({ lat: 0, lng: 0, accuracy: 0 });
  const watchIdRef = useRef(null);

  // Fetch organizzazione utente
  useEffect(() => {
    const fetchMyOrg = async () => {
      if (!token) return;
      try {
        const res = await axios.get(`${BACKEND_URL}/api/my-organization`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.has_organization) {
          setMyOrganization(res.data);
        }
      } catch (err) {
        console.log('No organization');
      }
    };
    fetchMyOrg();
  }, [token]);

  // Traccia visite e carica statistiche (solo admin)
  useEffect(() => {
    const trackVisitAndLoadStats = async () => {
      if (!token) return;
      
      // Traccia visita
      try {
        await axios.post(`${BACKEND_URL}/api/analytics/visit`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (err) {
        console.log('Visit tracking error');
      }
      
      // Carica statistiche se admin
      if (isAdmin) {
        try {
          const res = await axios.get(`${BACKEND_URL}/api/analytics/stats`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setAnalyticsStats(res.data);
        } catch (err) {
          console.log('Stats load error');
        }
      }
    };
    
    trackVisitAndLoadStats();
    
    // Aggiorna statistiche ogni 30 secondi se admin
    const interval = setInterval(() => {
      if (isAdmin && token) {
        axios.post(`${BACKEND_URL}/api/analytics/visit`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => {});
        
        axios.get(`${BACKEND_URL}/api/analytics/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(res => setAnalyticsStats(res.data)).catch(() => {});
      }
    }, 30000);
    
    return () => clearInterval(interval);
  }, [token, isAdmin]);

  // Carica statistiche immediatamente quando isAdmin diventa true
  useEffect(() => {
    if (isAdmin && token && !analyticsStats) {
      axios.get(`${BACKEND_URL}/api/analytics/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => setAnalyticsStats(res.data)).catch(() => {});
    }
  }, [isAdmin, token, analyticsStats]);

  // Mostra popup donazione ogni 15 ingressi (se non ha donato)
  useEffect(() => {
    const checkDonationPopup = () => {
      const hasDonated = localStorage.getItem('respiro_donated');
      
      // Se ha già donato, non mostrare il popup
      if (hasDonated === 'true') {
        return;
      }
      
      // Incrementa contatore ingressi
      const visitCount = parseInt(localStorage.getItem('respiro_visit_count') || '0', 10) + 1;
      localStorage.setItem('respiro_visit_count', visitCount.toString());
      
      // Mostra popup ogni 15 ingressi (1, 16, 31, ...)
      if (visitCount === 1 || visitCount % 15 === 1) {
        setTimeout(() => {
          setShowDonationPopup(true);
        }, 2000);
      }
    };
    
    // Controlla solo dopo che l'app è caricata
    if (!nameLoading && volunteerName) {
      checkDonationPopup();
    }
  }, [nameLoading, volunteerName]);

  // Mostra popup benvenuto Emme (una volta per sessione)
  useEffect(() => {
    const checkEmmeWelcome = () => {
      const hasSeenEmme = sessionStorage.getItem('respiro_emme_welcomed');
      
      if (hasSeenEmme === 'true') {
        return;
      }
      
      // Mostra dopo 3 secondi (dopo il popup donazione se c'è)
      setTimeout(() => {
        setShowEmmeWelcome(true);
        sessionStorage.setItem('respiro_emme_welcomed', 'true');
      }, 4000);
    };
    
    if (!nameLoading && volunteerName) {
      checkEmmeWelcome();
    }
  }, [nameLoading, volunteerName]);

  // Fetch notification count
  const fetchNotifications = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/notifications/count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const newCount = response.data.total;
      
      // Se ci sono nuove notifiche, mostra notifica nativa
      if (newCount > prevNotificationCount.current && prevNotificationCount.current >= 0) {
        const diff = newCount - prevNotificationCount.current;
        if (diff > 0 && prevNotificationCount.current > 0) {
          // Mostra notifica push nativa con vibrazione
          await showNativeNotification(
            '🌊 Respiro Blu',
            `${diff} nuove attività dalla community!`,
            newCount
          );
        }
      }
      
      prevNotificationCount.current = newCount;
      setNotificationCount(newCount);
      updateAppBadge(newCount);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, [token]);

  // Segna notifiche come lette
  const markNotificationsRead = useCallback(async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/notifications/mark-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotificationCount(0);
      updateAppBadge(0);
      prevNotificationCount.current = 0;
    } catch (error) {
      console.error('Error marking notifications:', error);
    }
  }, [token]);

  // Fetch event invites
  const fetchEventInvites = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/my-invites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEventInvites(response.data);
    } catch (error) {
      console.error('Error fetching invites:', error);
    }
  }, [token]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/collections/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [token]);

  // Fetch volunteer name
  const fetchVolunteerName = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/profile/name`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.name) {
        setVolunteerName(response.data.name);
      } else {
        // Se non c'è nome nel DB ma l'utente ha un displayName da Firebase, usalo
        if (user?.displayName) {
          // Auto-salva il nome da Firebase
          await axios.post(`${BACKEND_URL}/api/profile/name`, 
            { name: user.displayName },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setVolunteerName(user.displayName);
        } else {
          setShowNameModal(true);
        }
      }
    } catch (error) {
      console.error('Error fetching name:', error);
      // In caso di errore, prova con displayName di Firebase
      if (user?.displayName) {
        setVolunteerName(user.displayName);
      } else {
        setShowNameModal(true);
      }
    } finally {
      setNameLoading(false);
    }
  }, [token, user]);

  // Check if user is admin
  const checkAdminStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/check`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsAdmin(response.data.is_admin);
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
    }
  }, [token]);

  // GPS tracking - scrive nel REF, non nello STATE
  useEffect(() => {
    if (!('geolocation' in navigator)) return;

    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        gpsRef.current = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy
        };
      },
      (error) => {
        console.error('GPS error:', error);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 5000 }
    );

    return () => {
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, []);

  useEffect(() => {
    fetchStats();
    fetchVolunteerName();
    fetchNotifications();
    fetchEventInvites();
    checkAdminStatus();
    
    // Richiedi permesso notifiche
    requestNotificationPermission();
    
    // Controlla notifiche ogni 30 secondi
    const notificationInterval = setInterval(fetchNotifications, 30000);
    // Controlla inviti ogni 60 secondi
    const invitesInterval = setInterval(fetchEventInvites, 60000);
    
    return () => {
      clearInterval(notificationInterval);
      clearInterval(invitesInterval);
    };
  }, [fetchStats, fetchVolunteerName, fetchNotifications, fetchEventInvites, checkAdminStatus]);

  const saveVolunteerName = useCallback(async (profileData) => {
    try {
      // Se è un oggetto con più campi (nuovo formato)
      const data = typeof profileData === 'string' 
        ? { name: profileData } 
        : profileData;
      
      await axios.post(
        `${BACKEND_URL}/api/profile/name`,
        data,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVolunteerName(data.name);
      setShowNameModal(false);
      toast.success(`Benvenuto, ${data.name}!`);
    } catch (error) {
      console.error('Error saving name:', error);
    }
  }, [token]);

  // Handler per form - usa GPS dal REF
  const handleFlexibleSubmit = useCallback(async (data) => {
    try {
      const location = gpsRef.current.lat !== 0 ? gpsRef.current : { lat: 0, lng: 0 };
      
      await axios.post(
        `${BACKEND_URL}/api/collections`,
        {
          kg_collected: data.total_kg,
          location: location,
          generic_kg: data.generic,
          categories: data.categories,
          photo_url: data.photo || null,
          place: data.beach_name || '',
          difficulty: data.difficulty || '',
          has_signage: data.has_signage,
          pollution_level: data.pollution_level || '',
          animal_sightings: data.animal_sightings || '',
          // ESG data
          hours_spent: data.hours_spent || 0,
          esg_environmental: data.esg_environmental || 0,
          esg_social: data.esg_social || 0,
          esg_governance: data.esg_governance || true,
          // Fishing for Litter fields
          is_sea_collection: data.is_sea_collection || false,
          participants_count: data.participants_count || 1
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Raccolta salvata con successo!');
      fetchStats();
    } catch (error) {
      console.error(error);
      toast.error('Errore nel salvataggio');
    }
  }, [token, fetchStats]);

  const goToHome = useCallback(() => {
    setCurrentView('gamification'); // Nuova home = gamification
    setShowNavMenu(false);
  }, []);

  const navigateToSection = useCallback((sectionId) => {
    setCurrentView(sectionId);
    setShowNavMenu(false);
  }, []);

  const [showNotificationBanner, setShowNotificationBanner] = useState(false);

  // Verifica se mostrare banner notifiche
  useEffect(() => {
    if (isNotificationSupported() && window.Notification.permission === 'default') {
      setShowNotificationBanner(true);
    }
  }, []);

  const enableNotifications = async () => {
    const granted = await requestNotificationPermission();
    if (granted) {
      toast.success('Notifiche attivate! Riceverai aggiornamenti sulle nuove attività.');
      // Test notifica
      await showNativeNotification(
        '🌊 Respiro Blu',
        'Notifiche attivate con successo!',
        0
      );
    }
    setShowNotificationBanner(false);
  };

  // Menu items con traduzioni e descrizioni
  const getNavItems = () => {
    const items = [
      { id: 'home', label: t('nav.home'), icon: Home, color: 'cyan', description: 'Dashboard principale con statistiche e accesso rapido alle funzioni' },
      { id: 'comunicazioni', label: 'Comunicazioni', icon: Megaphone, color: 'violet', description: 'Annunci, protocolli e aggiornamenti dalla community' },
      { id: 'storico', label: t('nav.history'), icon: History, color: 'purple', description: 'Storico delle tue raccolte rifiuti con statistiche e certificati PDF' },
      { id: 'calendario', label: t('nav.calendar'), icon: Calendar, color: 'indigo', description: 'Eventi, pulizie di spiaggia e attività della community' },
      { id: 'luoghi', label: t('nav.places'), icon: Compass, color: 'emerald', description: 'Punti di interesse: spiagge, aree protette e luoghi da esplorare' },
      { id: 'fauna', label: t('nav.floraFauna'), icon: Sprout, color: 'green', description: 'Guida alla flora e fauna marina del Mediterraneo' },
      { 
        id: 'maredu', 
        label: 'MareDu - Scuole', 
        icon: GraduationCap, 
        color: 'lime', 
        description: 'Educazione ambientale nelle scuole: laboratori e certificati CO₂',
        subMenu: [
          { id: 'maredu-labs', label: 'Laboratori', description: 'Gestione laboratori educativi e certificati ESG' },
          { id: 'maredu-questionario', label: 'Questionario', description: 'Questionario post-laboratorio per studenti' }
        ]
      },
      { id: 'rov', label: 'ROV', icon: Radar, color: 'blue', description: 'Missioni ROV subacquee per mappare reti fantasma' },
      { id: 'rivista', label: t('nav.magazine'), icon: BookOpen, color: 'pink', description: 'Articoli, notizie e approfondimenti sul mare' },
      { 
        id: 'ambiente', 
        label: 'Ambiente', 
        icon: CloudSun, 
        color: 'sky', 
        description: 'Condizioni meteo, fasi lunari, maree e temperature marine',
        subMenu: [
          { id: 'meteo', label: t('nav.weather'), description: 'Previsioni meteo marine: onde, vento e temperatura' },
          { id: 'mareluna', label: 'Mare & Luna', description: 'Fasi lunari, maree e temperature del mare' }
        ]
      },
      { id: 'mare', label: 'Mare', icon: Ship, color: 'teal', description: 'Fishing for Litter: raccolta rifiuti con i pescatori' },
      { id: 'segnala', label: 'Segnala', icon: AlertTriangle, color: 'yellow', description: 'Segnala rifiuti o situazioni di degrado ambientale' },
      { id: 'subacquei', label: 'Sub', icon: Waves, color: 'blue', description: 'Sezione dedicata ai subacquei e alle immersioni' },
      { id: 'mappa', label: t('nav.map'), icon: Map, color: 'violet', description: 'Mappa interattiva con segnalazioni e punti di interesse' },
      { id: 'sos', label: t('nav.sos'), icon: Siren, color: 'red', description: 'Emergenze: numeri utili e segnalazioni urgenti' },
      { id: 'profilo', label: t('nav.profile'), icon: User, color: 'slate', description: 'Il tuo profilo, statistiche personali e impostazioni' },
      { id: 'attivita', label: 'Attività', icon: TrendingDown, color: 'orange', description: 'Riepilogo delle tue attività e contributi' },
      { id: 'info', label: 'Info', icon: Info, color: 'gray', description: 'Informazioni sul progetto Respiro Blu' },
      { id: 'community', label: 'Community', icon: Users, color: 'rose', description: 'La community di Citizen Science per il Mediterraneo' }
    ];
    
    if (isAdmin) {
      items.push({ id: 'admin', label: t('nav.admin'), icon: Shield, color: 'amber', description: 'Pannello amministrazione: gestione utenti e contenuti' });
    }
    
    return items;
  };
  
  const navItems = getNavItems();
  
  // Stato per popup descrizione e sottomenu
  const [menuPopup, setMenuPopup] = useState({ show: false, item: null, x: 0, y: 0 });
  const [showSubMenu, setShowSubMenu] = useState(null);
  const longPressTimer = useRef(null);
  
  // Gestione long press
  const handleMenuTouchStart = (item, e) => {
    const touch = e.touches?.[0] || e;
    longPressTimer.current = setTimeout(() => {
      setMenuPopup({ 
        show: true, 
        item, 
        x: touch.clientX || window.innerWidth / 2,
        y: touch.clientY || window.innerHeight / 2
      });
    }, 500); // 500ms per long press
  };
  
  const handleMenuTouchEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
    }
  };
  
  const handleMenuClick = (item) => {
    // Se ha sottomenu, mostra sottomenu
    if (item.subMenu) {
      setShowSubMenu(showSubMenu === item.id ? null : item.id);
    } else {
      setCurrentView(item.id);
      setShowSubMenu(null);
      setShowNavMenu(false);
    }
  };
  
  const handleSubMenuClick = (subItem) => {
    if (subItem.id === 'maredu-labs') {
      setCurrentView('maredu');
    } else if (subItem.id === 'maredu-questionario') {
      setCurrentView('maredu-questionario');
    } else if (subItem.id === 'meteo') {
      setCurrentView('meteo');
    } else if (subItem.id === 'mareluna') {
      setCurrentView('mareluna');
    }
    setShowSubMenu(null);
    setShowNavMenu(false);
  };
  
  // Chiudi popup descrizione
  const closeMenuPopup = () => {
    setMenuPopup({ show: false, item: null, x: 0, y: 0 });
  };

  if (nameLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-slate-400">Caricamento...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 pb-24">
      {/* Banner Attiva Notifiche */}
      {showNotificationBanner && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-cyan-600 to-emerald-600 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3 text-white">
            <Bell className="w-5 h-5" />
            <span className="text-sm font-medium">Attiva le notifiche per ricevere aggiornamenti</span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={enableNotifications}
              className="px-4 py-1.5 bg-white text-cyan-600 rounded-full text-sm font-bold hover:bg-cyan-50 transition-all"
            >
              Attiva
            </button>
            <button
              onClick={() => setShowNotificationBanner(false)}
              className="px-3 py-1.5 text-white/80 hover:text-white text-sm"
            >
              No grazie
            </button>
          </div>
        </div>
      )}

      {/* Badge Admin Stats - Solo per admin */}
      {isAdmin && analyticsStats && (
        <div className={`fixed ${showNotificationBanner ? 'top-16' : 'top-4'} left-4 z-50 flex items-center gap-3 px-4 py-2 bg-amber-500/20 border border-amber-500/50 text-amber-400 rounded-full shadow-lg backdrop-blur-sm`}>
          <div className="flex items-center gap-1.5">
            <span className="text-sm">👁️</span>
            <span className="text-xs font-bold">{analyticsStats.total_visits}</span>
          </div>
          <div className="w-px h-4 bg-amber-500/30"></div>
          <div className="flex items-center gap-1.5">
            <span className="text-sm">📅</span>
            <span className="text-xs font-bold">{analyticsStats.today_visits}</span>
          </div>
          <div className="w-px h-4 bg-amber-500/30"></div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
            <span className="text-xs font-bold">{analyticsStats.online_count}</span>
          </div>
        </div>
      )}

      {/* Indicatore Notifiche Fisso */}
      {notificationCount > 0 && (
        <button
          onClick={markNotificationsRead}
          className={`fixed ${showNotificationBanner ? 'top-16' : 'top-4'} right-4 z-50 flex items-center gap-2 px-3 py-2 bg-cyan-500 text-white rounded-full shadow-lg animate-pulse`}
        >
          <Bell className="w-4 h-4" />
          <span className="font-bold">{notificationCount}</span>
          <span className="text-xs">nuove</span>
        </button>
      )}

      {/* Indicatore Inviti Eventi */}
      {eventInvites.length > 0 && (
        <div className={`fixed ${showNotificationBanner ? 'top-16' : 'top-4'} ${notificationCount > 0 ? 'right-32' : 'right-4'} z-50`}>
          <button
            onClick={() => setShowInvitesDropdown(!showInvitesDropdown)}
            className="flex items-center gap-2 px-3 py-2 bg-violet-500 text-white rounded-full shadow-lg animate-pulse"
          >
            <Calendar className="w-4 h-4" />
            <span className="font-bold">{eventInvites.length}</span>
            <span className="text-xs">inviti</span>
          </button>
          
          {/* Dropdown Inviti */}
          {showInvitesDropdown && (
            <div className="absolute top-12 right-0 w-80 bg-slate-900 border border-violet-500/50 rounded-xl shadow-2xl overflow-hidden">
              <div className="p-3 bg-violet-500/20 border-b border-violet-500/30">
                <h3 className="text-violet-400 font-bold text-sm">INVITI AD ATTIVITÀ</h3>
              </div>
              <div className="max-h-64 overflow-y-auto">
                {eventInvites.map((invite, idx) => (
                  <div key={idx} className="p-3 border-b border-slate-800 hover:bg-slate-800/50">
                    <p className="text-white font-medium text-sm">{invite.event_title}</p>
                    <p className="text-slate-400 text-xs mt-1">
                      📅 {new Date(invite.event_date).toLocaleDateString('it-IT')} alle {invite.event_time}
                    </p>
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={() => {
                          setCurrentView('attivita');
                          setShowInvitesDropdown(false);
                        }}
                        className="flex-1 py-1.5 bg-violet-500 hover:bg-violet-600 text-white text-xs font-medium rounded-lg transition-all"
                      >
                        Vai all'evento
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              <button 
                onClick={() => setShowInvitesDropdown(false)}
                className="w-full p-2 text-slate-500 text-xs hover:bg-slate-800 transition-all"
              >
                Chiudi
              </button>
            </div>
          )}
        </div>
      )}

      {/* HOME */}
      {currentView === 'home' && (
        <div className="max-w-4xl mx-auto p-6">
          {/* Guida Emme per Raccolta */}
          {showHomeGuide && <EmmeGuide sectionId="raccolta" onClose={closeHomeGuide} />}
          <EmmeHelpButton onClick={openHomeGuide} />
          
          {/* Header - statico, non si ri-renderizza */}
          <div className="mb-8 text-center relative">
            {/* Selettore Lingua - In alto a destra */}
            <div className="absolute top-0 right-0">
              <LanguageSelector />
            </div>
            
            {/* Logo con eventuale organizzazione */}
            <div className="flex justify-center items-center gap-4 mb-6">
              <img 
                src="/logo-respiro-blu.png" 
                alt="Respiro Blu Logo" 
                className="w-32 h-32 rounded-2xl object-cover shadow-[0_0_30px_rgba(6,182,212,0.3)]"
              />
              {myOrganization && (
                <>
                  <span className="text-slate-500 text-2xl font-light">per</span>
                  {myOrganization.organization_logo ? (
                    <img 
                      src={myOrganization.organization_logo} 
                      alt={myOrganization.organization_name}
                      className="w-32 h-32 rounded-2xl object-contain bg-white p-2 shadow-[0_0_30px_rgba(16,185,129,0.3)]"
                    />
                  ) : (
                    <div className="w-32 h-32 rounded-2xl bg-emerald-500/20 border border-emerald-500/50 flex items-center justify-center">
                      <span className="text-emerald-400 font-bold text-xl text-center px-2">
                        {myOrganization.organization_name}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
            {myOrganization && (
              <p className="text-emerald-400 text-sm mb-2">
                RESPIRO BLU per <span className="font-bold">{myOrganization.organization_name}</span>
              </p>
            )}
            <h2 className="text-4xl md:text-5xl font-bold text-white" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>SALVA IL MARE</h2>
            
            {/* Badge Citizen Science */}
            <div className="mt-3 mb-2 inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/40 rounded-full">
              <span className="text-sm">🔬</span>
              <span className="text-cyan-300 text-xs font-medium tracking-wide">CITIZEN SCIENCE</span>
            </div>
            
            {volunteerName && (
              <p className="text-cyan-400 mt-2 text-lg">Volontario: <span className="font-semibold">{volunteerName}</span></p>
            )}
            <p className="text-slate-400 mt-1">Trasforma le tue azioni in dati scientifici per il Mediterraneo</p>
            
            {/* GPS Badge isolato */}
            <GPSBadge gpsRef={gpsRef} />
          </div>

          <h3 className="text-xl font-semibold text-white mb-4" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>REGISTRA RACCOLTA</h3>

          {/* Form isolato - NON si ri-renderizza con GPS */}
          <div className="mb-8">
            <FlexibleInputForm onSubmit={handleFlexibleSubmit} />
          </div>

          <h3 className="text-xl font-semibold text-white mb-4" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>IL TUO IMPATTO</h3>
          
          <StatsCards stats={stats} t={t} />

          {/* Sezione Citizen Science */}
          <div className="my-6 bg-gradient-to-br from-slate-900/80 to-cyan-900/20 border border-cyan-500/30 rounded-2xl p-5">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">🔬</span>
              </div>
              <div>
                <h4 className="text-white font-bold text-lg mb-1">
                  <TranslatedText text="Citizen Science" />
                </h4>
                <p className="text-slate-300 text-sm leading-relaxed">
                  <TranslatedText text="Con Respiro Blu diventi uno scienziato cittadino. Ogni raccolta, foto e segnalazione genera dati geolocalizzati che contribuiscono alla ricerca sulla salute del Mediterraneo." />
                </p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <span className="px-2 py-1 bg-cyan-500/20 text-cyan-300 text-xs rounded-full">
                    <TranslatedText text="Dati GPS" />
                  </span>
                  <span className="px-2 py-1 bg-emerald-500/20 text-emerald-300 text-xs rounded-full">
                    <TranslatedText text="Foto validate" />
                  </span>
                  <span className="px-2 py-1 bg-violet-500/20 text-violet-300 text-xs rounded-full">
                    <TranslatedText text="Categorizzazione" />
                  </span>
                  <span className="px-2 py-1 bg-amber-500/20 text-amber-300 text-xs rounded-full">
                    <TranslatedText text="Open Data" />
                  </span>
                </div>
              </div>
            </div>
          </div>

          <h3 className="text-xl font-semibold text-white mb-4" style={{fontFamily: 'Barlow Condensed, sans-serif'}}><TranslatedText text="AZIONI RAPIDE" /></h3>
          <button
            type="button"
            onClick={() => navigateToSection('mappa')}
            className="w-full bg-slate-900/60 border border-emerald-500/50 rounded-2xl p-6 hover:border-emerald-500 transition-all flex items-center justify-between"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <Map className="w-6 h-6 text-emerald-400" />
              </div>
              <div className="text-left">
                <h4 className="text-lg font-semibold text-white"><TranslatedText text="Mappa Raccolte" /></h4>
                <p className="text-sm text-slate-400"><TranslatedText text="Visualizza tutte le raccolte" /></p>
              </div>
            </div>
          </button>
        </div>
      )}

      {/* OTHER VIEWS */}
      {currentView === 'gamification' && (
        <GamificationSection 
          onNavigateToOldHome={() => setCurrentView('home')} 
          onStartCollection={() => setCurrentView('home')}
        />
      )}
      {currentView === 'storico' && <StoricoSection />}
      {currentView === 'comunicazioni' && <AnnouncementSection />}
      {currentView === 'calendario' && <CalendarSection />}
      {currentView === 'luoghi' && <PlacesSection />}
      {currentView === 'fauna' && <FloraFaunaSection />}
      {currentView === 'rivista' && <MagazineSection />}
      {currentView === 'meteo' && <MeteoMareSection />}
      {currentView === 'mareluna' && <MareLunaSection />}
      {currentView === 'mare' && <FishingForLitterSection />}
      {currentView === 'segnala' && <SegnalazioniSection />}
      {currentView === 'subacquei' && <SubacqueiSection />}
      {currentView === 'maredu' && <MareDuSection />}
      {currentView === 'maredu-questionario' && <QuestionnaireForm />}
      {currentView === 'rov' && <ROVSection />}
      {currentView === 'mappa' && <MapSection />}
      {currentView === 'sos' && <SOSSection gpsLocation={gpsRef.current} onGoHome={goToHome} />}
      {currentView === 'profilo' && (
        <>
          <div className="max-w-4xl mx-auto px-4 pt-4">
            <MyOrganizationArea />
          </div>
          <ProfiloSection volunteerName={volunteerName} onNameChange={setVolunteerName} />
        </>
      )}
      {currentView === 'attivita' && <AttivitaSection />}
      {currentView === 'info' && <InfoSection onClose={goToHome} />}
      {currentView === 'community' && <CommunitySection />}
      {currentView === 'admin' && isAdmin && <AdminSection />}

      {showNameModal && <VolunteerNameModal onSave={saveVolunteerName} />}

      {/* Popup Donazione */}
      {showDonationPopup && (
        <DonationPopup onClose={() => setShowDonationPopup(false)} />
      )}

      {/* Bottom Nav con Menu Dropdown */}
      <nav className="fixed bottom-0 left-0 right-0 bg-slate-900/95 backdrop-blur-xl border-t border-white/10 z-40">
        <div className="max-w-screen-xl mx-auto">
          {/* Overlay per chiudere sottomenu quando si tocca fuori */}
          {showSubMenu && (
            <div 
              className="fixed inset-0 z-30" 
              onClick={() => setShowSubMenu(null)}
            />
          )}
          
          {/* Menu Dropdown */}
          {showNavMenu && (
            <div className="absolute bottom-full left-0 right-0 bg-slate-950 border-t border-cyan-500/30 p-4 shadow-2xl max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-4 gap-3 max-w-md mx-auto">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const active = currentView === item.id || (item.subMenu && item.subMenu.some(s => currentView === s.id.replace('maredu-', 'maredu-')));
                  const hasSubMenu = item.subMenu && item.subMenu.length > 0;
                  const isSubMenuOpen = showSubMenu === item.id;
                  
                  const colorClasses = {
                    cyan: active ? 'text-cyan-400 bg-cyan-500/20' : 'text-cyan-500 bg-cyan-500/10',
                    purple: active ? 'text-purple-400 bg-purple-500/20' : 'text-purple-500 bg-purple-500/10',
                    indigo: active ? 'text-indigo-400 bg-indigo-500/20' : 'text-indigo-500 bg-indigo-500/10',
                    emerald: active ? 'text-emerald-400 bg-emerald-500/20' : 'text-emerald-500 bg-emerald-500/10',
                    green: active ? 'text-green-400 bg-green-500/20' : 'text-green-500 bg-green-500/10',
                    lime: active ? 'text-lime-400 bg-lime-500/20' : 'text-lime-500 bg-lime-500/10',
                    pink: active ? 'text-pink-400 bg-pink-500/20' : 'text-pink-500 bg-pink-500/10',
                    sky: active ? 'text-sky-400 bg-sky-500/20' : 'text-sky-500 bg-sky-500/10',
                    teal: active ? 'text-teal-400 bg-teal-500/20' : 'text-teal-500 bg-teal-500/10',
                    yellow: active ? 'text-yellow-400 bg-yellow-500/20' : 'text-yellow-500 bg-yellow-500/10',
                    blue: active ? 'text-blue-400 bg-blue-500/20' : 'text-blue-500 bg-blue-500/10',
                    violet: active ? 'text-violet-400 bg-violet-500/20' : 'text-violet-500 bg-violet-500/10',
                    red: active ? 'text-red-400 bg-red-500/20' : 'text-red-500 bg-red-500/10',
                    slate: active ? 'text-slate-300 bg-slate-600/20' : 'text-slate-400 bg-slate-500/10',
                    orange: active ? 'text-orange-400 bg-orange-500/20' : 'text-orange-500 bg-orange-500/10',
                    gray: active ? 'text-gray-300 bg-gray-500/20' : 'text-gray-400 bg-gray-500/10',
                    amber: active ? 'text-amber-400 bg-amber-500/20' : 'text-amber-500 bg-amber-500/10',
                    rose: active ? 'text-rose-400 bg-rose-500/20' : 'text-rose-500 bg-rose-500/10'
                  };
                  
                  return (
                    <div key={item.id} className="relative">
                      <button
                        type="button"
                        onClick={() => handleMenuClick(item)}
                        onTouchStart={(e) => handleMenuTouchStart(item, e)}
                        onTouchEnd={handleMenuTouchEnd}
                        onMouseDown={(e) => handleMenuTouchStart(item, e)}
                        onMouseUp={handleMenuTouchEnd}
                        onMouseLeave={handleMenuTouchEnd}
                        className={`w-full flex flex-col items-center gap-1 p-3 rounded-xl transition-all ${colorClasses[item.color]} ${hasSubMenu ? 'ring-1 ring-white/10' : ''}`}
                      >
                        <Icon className="w-6 h-6" />
                        <span className="text-xs font-medium">{item.label}</span>
                        {hasSubMenu && (
                          <span className="absolute top-1 right-1 w-2 h-2 bg-white/30 rounded-full"></span>
                        )}
                      </button>
                      
                      {/* Sottomenu */}
                      {hasSubMenu && isSubMenuOpen && (
                        <div className="absolute bottom-full left-0 right-0 mb-2 bg-slate-800 border border-lime-500/30 rounded-xl overflow-hidden shadow-xl z-50 min-w-[180px]">
                          {item.subMenu.map((subItem) => (
                            <button
                              key={subItem.id}
                              type="button"
                              onClick={() => handleSubMenuClick(subItem)}
                              onTouchStart={(e) => handleMenuTouchStart(subItem, e)}
                              onTouchEnd={handleMenuTouchEnd}
                              onMouseDown={(e) => handleMenuTouchStart(subItem, e)}
                              onMouseUp={handleMenuTouchEnd}
                              onMouseLeave={handleMenuTouchEnd}
                              className="w-full px-4 py-3 text-left hover:bg-lime-500/20 transition-all border-b border-slate-700/50"
                            >
                              <span className="text-lime-300 text-sm font-medium block">{subItem.label}</span>
                              <span className="text-slate-400 text-xs">{subItem.description}</span>
                            </button>
                          ))}
                          {/* Pulsante Chiudi */}
                          <button
                            type="button"
                            onClick={() => setShowSubMenu(null)}
                            className="w-full px-4 py-2 text-center text-slate-500 text-xs hover:bg-slate-700/50 transition-all"
                          >
                            ✕ Chiudi
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Popup Descrizione Long Press */}
          {menuPopup.show && menuPopup.item && (
            <div 
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
              onClick={closeMenuPopup}
            >
              <div 
                className="bg-slate-900 border border-cyan-500/50 rounded-2xl p-5 mx-4 max-w-sm shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center gap-3 mb-3">
                  {menuPopup.item.icon && (() => {
                    const IconComponent = menuPopup.item.icon;
                    return <IconComponent className="w-8 h-8 text-cyan-400" />;
                  })()}
                  <h3 className="text-white font-bold text-lg">{menuPopup.item.label}</h3>
                </div>
                <p className="text-slate-300 text-sm leading-relaxed mb-4">
                  {menuPopup.item.description}
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      if (menuPopup.item.subMenu) {
                        setShowSubMenu(menuPopup.item.id);
                      } else if (menuPopup.item.id.startsWith('maredu-')) {
                        handleSubMenuClick(menuPopup.item);
                      } else {
                        setCurrentView(menuPopup.item.id);
                        setShowNavMenu(false);
                      }
                      closeMenuPopup();
                    }}
                    className="flex-1 py-2.5 bg-cyan-500 hover:bg-cyan-600 text-white font-medium rounded-xl transition-all"
                  >
                    Apri
                  </button>
                  <button
                    type="button"
                    onClick={closeMenuPopup}
                    className="px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-slate-300 font-medium rounded-xl transition-all"
                  >
                    Chiudi
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Barra principale con pulsanti rapidi + Menu */}
          <div className="flex items-center justify-between py-2 px-3">
            {/* Pulsanti rapidi (Home, Mappa, Ambiente, Rivista) */}
            <div className="flex items-center gap-1 relative">
              {[
                { id: 'gamification', label: 'Sfide', icon: Target, color: 'cyan' },
                { id: 'mappa', label: t('nav.map'), icon: Map, color: 'violet' },
                { id: 'ambiente', label: 'Ambiente', icon: CloudSun, color: 'sky', hasDropdown: true },
                { id: 'rivista', label: t('nav.magazine'), icon: BookOpen, color: 'pink' }
              ].map((item) => {
                const Icon = item.icon;
                const active = currentView === item.id || (item.id === 'ambiente' && (currentView === 'meteo' || currentView === 'mareluna'));
                const colorClasses = {
                  cyan: active ? 'text-cyan-400 bg-cyan-500/10' : 'text-cyan-500',
                  violet: active ? 'text-violet-400 bg-violet-500/10' : 'text-violet-500',
                  sky: active ? 'text-sky-400 bg-sky-500/10' : 'text-sky-500',
                  pink: active ? 'text-pink-400 bg-pink-500/10' : 'text-pink-500'
                };
                
                if (item.hasDropdown) {
                  return (
                    <div key={item.id} className="relative">
                      <button
                        type="button"
                        data-testid={`nav-${item.id}-button`}
                        onClick={() => setShowSubMenu(showSubMenu === 'quick-ambiente' ? null : 'quick-ambiente')}
                        className={`flex flex-col items-center gap-0.5 px-3 py-2 rounded-xl transition-all ${colorClasses[item.color]}`}
                      >
                        <Icon className="w-5 h-5" />
                        <span className="text-[10px] font-medium">{item.label}</span>
                      </button>
                      {showSubMenu === 'quick-ambiente' && (
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-slate-800 border border-sky-500/30 rounded-xl overflow-hidden shadow-xl z-50 min-w-[140px]">
                          <button
                            type="button"
                            onClick={() => { setCurrentView('meteo'); setShowSubMenu(null); }}
                            className="w-full px-4 py-3 text-left hover:bg-sky-500/20 transition-all border-b border-slate-700/50 flex items-center gap-2"
                          >
                            <CloudSun className="w-4 h-4 text-sky-400" />
                            <span className="text-sky-300 text-sm font-medium">Meteo</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => { setCurrentView('mareluna'); setShowSubMenu(null); }}
                            className="w-full px-4 py-3 text-left hover:bg-purple-500/20 transition-all border-b border-slate-700/50 flex items-center gap-2"
                          >
                            <Moon className="w-4 h-4 text-purple-400" />
                            <span className="text-purple-300 text-sm font-medium">Mare & Luna</span>
                          </button>
                          {/* Pulsante Chiudi */}
                          <button
                            type="button"
                            onClick={() => setShowSubMenu(null)}
                            className="w-full px-3 py-2 text-center text-slate-500 text-xs hover:bg-slate-700/50 transition-all"
                          >
                            ✕ Chiudi
                          </button>
                        </div>
                      )}
                    </div>
                  );
                }
                
                return (
                  <button
                    type="button"
                    key={item.id}
                    data-testid={`nav-${item.id}-button`}
                    onClick={() => navigateToSection(item.id)}
                    className={`flex flex-col items-center gap-0.5 px-3 py-2 rounded-xl transition-all ${colorClasses[item.color]}`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-[10px] font-medium">{item.label}</span>
                  </button>
                );
              })}
            </div>
            
            {/* SOS sempre visibile */}
            <button
              type="button"
              onClick={() => navigateToSection('sos')}
              className={`flex flex-col items-center gap-0.5 px-3 py-2 rounded-xl transition-all text-red-500 ${
                currentView === 'sos' ? 'bg-red-500/10' : ''
              }`}
            >
              <Siren className="w-5 h-5" />
              <span className="text-[10px] font-medium">SOS</span>
            </button>
            
            {/* Pulsante Menu */}
            <button
              type="button"
              onClick={() => setShowNavMenu(!showNavMenu)}
              className={`flex flex-col items-center gap-0.5 px-3 py-2 rounded-xl transition-all ${
                showNavMenu ? 'text-cyan-400 bg-cyan-500/10' : 'text-slate-400'
              }`}
            >
              {showNavMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              <span className="text-[10px] font-medium">Menu</span>
            </button>
          </div>
        </div>
      </nav>

      {/* FOOTER COPYRIGHT */}
      <footer className="pb-24 pt-8">
        <div className="text-center px-4">
          <p className="text-xs text-slate-500 leading-relaxed max-w-xl mx-auto">
            © 2026 Respiro Blu. Progetto ideato, sviluppato e gestito da Massimo Marchiori — P. IVA 04056480231. Tutti i diritti riservati. Vietata la copia e l'utilizzo da parte di terzi senza autorizzazione.
          </p>
        </div>
      </footer>

      {/* Popup Comunicazioni Non Lette */}
      <UnreadAnnouncementPopup onNavigate={(section) => setCurrentView(section)} />

      {/* Emme AI Assistant Animato - animazione "tuffo" quando menu aperto */}
      <EmmeButton onClick={() => setShowTurtle(true)} isHiding={showNavMenu} />
      <EmmeAssistant isOpen={showTurtle} onClose={() => setShowTurtle(false)} />
      
      {/* Popup benvenuto Emme */}
      <EmmeWelcomePopup 
        isOpen={showEmmeWelcome} 
        onClose={() => setShowEmmeWelcome(false)}
        onOpenChat={() => setShowTurtle(true)}
      />
    </div>
  );
};

export default MainApp;
